import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { en } from '../i18n/en';
import { nl } from '../i18n/nl';
export default function Game({ lang }) {
    const canvasRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const t = lang === 'nl' ? nl.nav : en.nav;
    // Game constants
    const CANVAS_WIDTH = 600;
    const CANVAS_HEIGHT = 150;
    // Game constants
    const DINO_WIDTH = 30;
    const DINO_HEIGHT = 35;
    const BUG_WIDTH = 30;
    const BUG_HEIGHT = 30;
    const BLOCK_WIDTH = 30;
    const BLOCK_HEIGHT = 30;
    const COIN_SIZE = 24;
    const GRAVITY = 0.6;
    const JUMP_FORCE = -10;
    const GAME_SPEED = 5;
    const drawEmoji = (ctx, emoji, x, y, size) => {
        ctx.font = `${size}px "Segoe UI Emoji", "Apple Color Emoji", Arial, sans-serif`;
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';
        ctx.fillText(emoji, x, y);
    };
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        let animationId;
        let frameCount = 0;
        let nextObstacleFrame = 0;
        // Game state
        let dino = {
            x: 50,
            y: CANVAS_HEIGHT - DINO_HEIGHT,
            dy: 0,
            jumps: 0
        };
        let obstacles = [];
        let rewards = [];
        let currentScore = 0;
        const resetGame = () => {
            dino = { x: 50, y: CANVAS_HEIGHT - DINO_HEIGHT, dy: 0, jumps: 0 };
            obstacles = [];
            rewards = [];
            currentScore = 0;
            nextObstacleFrame = 60; // spawn first obstacle after 60 frames
            setScore(0);
            setGameOver(false);
            setIsPlaying(true);
        };
        const jump = () => {
            if (dino.jumps < 2) {
                dino.dy = JUMP_FORCE;
                dino.jumps++;
            }
        };
        // Keep handleKeyDown simple and ensure it plays nice with fast multi-press
        const handleAction = () => {
            if (gameOver) {
                resetGame();
            }
            else if (!isPlaying) {
                setIsPlaying(true);
                jump();
            }
            else {
                jump();
            }
        };
        const handleKeyDown = (e) => {
            if (e.code === 'Space' || e.code === 'ArrowUp') {
                const rect = canvas.getBoundingClientRect();
                const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (isInView || isPlaying) {
                    e.preventDefault();
                    handleAction();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown, { passive: false });
        const checkCollision = (obs) => {
            return (dino.x < obs.x + obs.width &&
                dino.x + DINO_WIDTH > obs.x &&
                dino.y < obs.y + obs.height &&
                dino.y + DINO_HEIGHT > obs.y);
        };
        const checkRewardCollision = (reward) => {
            const rx = reward.x;
            const ry = reward.y;
            return (dino.x < rx + COIN_SIZE &&
                dino.x + DINO_WIDTH > rx &&
                dino.y < ry + COIN_SIZE &&
                dino.y + DINO_HEIGHT > ry);
        };
        const gameLoop = () => {
            if (!isPlaying || gameOver) {
                if (!isPlaying && !gameOver) {
                    // Draw start screen
                    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                    ctx.fillStyle = document.documentElement.classList.contains('dark') ? '#94a3b8' : '#64748b';
                    ctx.font = 'bold 24px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText('Press SPACE to start', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
                    // Draw static player
                    drawEmoji(ctx, '👨‍💻', dino.x, dino.y, DINO_HEIGHT);
                }
                animationId = requestAnimationFrame(gameLoop);
                return;
            }
            ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            // Update score
            frameCount++;
            if (frameCount % 10 === 0) {
                currentScore++;
                setScore(currentScore);
            }
            // Handle Dino physics
            dino.dy += GRAVITY;
            dino.y += dino.dy;
            // Ground collision
            if (dino.y >= CANVAS_HEIGHT - DINO_HEIGHT) {
                dino.y = CANVAS_HEIGHT - DINO_HEIGHT;
                dino.dy = 0;
                dino.jumps = 0;
            }
            // Draw Player
            drawEmoji(ctx, '👨‍💻', dino.x, dino.y, DINO_HEIGHT);
            // Spawn items (obstacles and rewards)
            if (frameCount >= nextObstacleFrame) {
                const randType = Math.random();
                if (randType < 0.25) {
                    // Spawn reward piece
                    rewards.push({
                        x: CANVAS_WIDTH,
                        y: CANVAS_HEIGHT - DINO_HEIGHT - 60 - Math.random() * 40,
                        radius: 10,
                        collected: false
                    });
                    nextObstacleFrame = frameCount + 30; // Rewards can spawn closer
                }
                else if (randType < 0.5) {
                    // Spawn floating block
                    obstacles.push({
                        x: CANVAS_WIDTH,
                        y: CANVAS_HEIGHT - DINO_HEIGHT - 40, // Low enough for normal jump, high enough to run under
                        width: BLOCK_WIDTH,
                        height: BLOCK_HEIGHT,
                        passed: false,
                        type: 'floating'
                    });
                    nextObstacleFrame = frameCount + 50 + Math.floor(Math.random() * 60);
                }
                else {
                    // Spawn ground bug
                    obstacles.push({
                        x: CANVAS_WIDTH,
                        y: CANVAS_HEIGHT - BUG_HEIGHT,
                        width: BUG_WIDTH,
                        height: BUG_HEIGHT,
                        passed: false,
                        type: 'ground'
                    });
                    nextObstacleFrame = frameCount + 80 + Math.floor(Math.random() * 60);
                }
            }
            // Update and draw rewards
            for (let i = rewards.length - 1; i >= 0; i--) {
                const r = rewards[i];
                r.x -= GAME_SPEED;
                if (!r.collected) {
                    drawEmoji(ctx, '⭐', r.x, r.y, COIN_SIZE);
                    if (checkRewardCollision(r)) {
                        r.collected = true;
                        currentScore += 100;
                        setScore(currentScore);
                    }
                }
                if (r.x + r.radius < 0) {
                    rewards.splice(i, 1);
                }
            }
            // Update and draw obstacles
            for (let i = obstacles.length - 1; i >= 0; i--) {
                const obs = obstacles[i];
                obs.x -= GAME_SPEED;
                if (obs.type === 'floating') {
                    drawEmoji(ctx, '🔥', obs.x, obs.y, BLOCK_HEIGHT);
                }
                else {
                    drawEmoji(ctx, '🐛', obs.x, obs.y, BUG_HEIGHT);
                }
                // Check collision
                if (checkCollision(obs)) {
                    setGameOver(true);
                    setHighScore(prev => Math.max(prev, currentScore));
                }
                // Remove off-screen obstacles
                if (obs.x + obs.width < 0) {
                    obstacles.splice(i, 1);
                }
            }
            // Draw Ground
            ctx.beginPath();
            ctx.moveTo(0, CANVAS_HEIGHT);
            ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT);
            ctx.strokeStyle = document.documentElement.classList.contains('dark') ? '#334155' : '#cbd5e1';
            ctx.lineWidth = 2;
            ctx.stroke();
            if (gameOver) {
                // Darkened background for game over screen
                ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
                ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                const isDark = document.documentElement.classList.contains('dark');
                // Game Over Box
                ctx.fillStyle = isDark ? '#1e293b' : '#ffffff';
                ctx.strokeStyle = isDark ? '#334155' : '#e2e8f0';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.roundRect((CANVAS_WIDTH / 2) - 150, (CANVAS_HEIGHT / 2) - 50, 300, 100, 10);
                ctx.fill();
                ctx.stroke();
                // Game Over Text
                ctx.fillStyle = isDark ? '#f1f5f9' : '#0f172a';
                ctx.font = 'bold 24px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('CRASHED! 💥', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 15);
                ctx.fillStyle = isDark ? '#94a3b8' : '#64748b';
                ctx.font = '16px Arial';
                ctx.fillText('Press SPACE to reboot', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20);
            }
            animationId = requestAnimationFrame(gameLoop);
        };
        animationId = requestAnimationFrame(gameLoop);
        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isPlaying, gameOver]);
    return (_jsxs("section", { id: "game", className: "pt-20 -mt-20", children: [_jsx("div", { className: "flex justify-between items-end mb-8 relative z-10", children: _jsxs("div", { children: [_jsxs("h2", { className: "text-3xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2", children: [_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", className: "h-8 w-8 text-primary-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M21 12a9 9 0 11-18 0 9 9 0 0118 0z" })] }), t.game] }), _jsx("div", { className: "h-1 w-20 bg-primary-500 mt-2 rounded" })] }) }), _jsxs("div", { className: "bg-white/50 dark:bg-slate-900/50 backdrop-blur rounded-xl border border-slate-200 dark:border-slate-800 p-6", children: [_jsxs("div", { className: "flex justify-between items-center mb-4", children: [_jsx("h3", { className: "text-xl font-bold gradient-text", children: "GAME TIME!" }), _jsxs("div", { className: "flex gap-4 font-mono text-sm", children: [_jsxs("span", { className: "text-slate-500", children: ["HI: ", highScore.toString().padStart(5, '0')] }), _jsx("span", { className: "font-bold text-slate-800 dark:text-slate-200", children: score.toString().padStart(5, '0') })] })] }), _jsx("div", { className: "border border-slate-200 dark:border-slate-700 rounded-lg overflow-x-auto bg-slate-50 dark:bg-slate-950 p-2 touch-none flex justify-center", children: _jsx("canvas", { ref: canvasRef, width: 600, height: 150, className: "block cursor-pointer", onClick: () => {
                                // Direct access to handleAction scope because logic captures latest jump state
                                // if they click the canvas, we just dispatch a space event to piggyback on the secure event listener
                                window.dispatchEvent(new KeyboardEvent('keydown', { code: 'Space' }));
                            } }) }), _jsxs("p", { className: "mt-4 text-center text-sm text-slate-500 dark:text-slate-400", children: ["Press ", _jsx("kbd", { className: "px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded mx-1", children: "Space" }), " or ", _jsx("kbd", { className: "px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded mx-1", children: "\u2191" }), " to jump. You can ", _jsx("strong", { children: "double jump" }), "! Grab ", _jsx("span", { className: "text-amber-500 font-bold", children: "coins" }), " for +100pts. Tap screen on mobile."] })] })] }));
}
